import { injectable } from "inversify";
import loadjs from "loadjs";
import { vintom } from "../../../api/globalVintomNamespace/GlobalVintomNamespace";
import { Resource } from "./Resource";

export interface ResourceLoader {
  loadResources(resources: Resource[], mayNotExist?: boolean): Promise<void>;
}

@injectable()
export class ResourceLoaderImpl implements ResourceLoader {
  loadResources(resources: Resource[], mayNotExist: boolean): Promise<void> {
    const resourcesToLoad = resources.filter((resource) => !vintom.requestedResources[resource.location]);
    const resourcesRequestedEarlier = resources.filter((resource) => vintom.requestedResources[resource.location]);
    const resourcesRequestedEarlierProgress = resourcesRequestedEarlier.map((resource) => vintom.requestedResources[resource.location]);

    const cssLocationsToLoad = resourcesToLoad.filter((resource) => resource.type === "CSS").map((resource) => resource.location);

    const jsLocationsToLoad = resourcesToLoad.filter((resource) => resource.type === "JS").map((resource) => resource.location);

    return Promise.all([
      ...resourcesRequestedEarlierProgress,
      this.loadCssResources(cssLocationsToLoad, mayNotExist),
      this.loadJsResources(jsLocationsToLoad, mayNotExist),
    ]).then(() => Promise.resolve());
  }

  private loadJsResources(locations: string[], mayNotExist: boolean): Promise<void> {
    if (!locations.length) {
      return Promise.resolve();
    }

    return Promise.all(locations.map((location) => this.loadJSResource(location, mayNotExist))).then(() => Promise.resolve());
  }

  private loadJSResource(location: string, mayNotExist: boolean): Promise<void> {
    const loadJsRequest = new Promise<void>((resolve, reject) => {
      loadjs([location], location, {
        error: () => (mayNotExist ? resolve() : reject()),
        success: resolve,
      });
    }).then(() => Promise.resolve());

    vintom.requestedResources[location] = loadJsRequest;
    return loadJsRequest;
  }

  private loadCssResources(locations: string[], mayNotExist: boolean): Promise<void> {
    return Promise.all(locations.map((location) => this.loadCssResource(location, mayNotExist))).then(() => Promise.resolve());
  }

  private loadCssResource(location: string, mayNotExist: boolean): Promise<void> {
    const link = document.createElement("link");
    link.setAttribute("rel", "stylesheet");
    link.setAttribute("type", "text/css");
    link.setAttribute("href", location);
    const loadCssRequest = new Promise<void>((resolve, reject) => {
      document.head.appendChild(link);
      link.onload = () => resolve();
      link.onerror = () => (mayNotExist ? resolve() : reject());
    }).then(() => Promise.resolve());

    vintom.requestedResources[location] = loadCssRequest;
    return loadCssRequest;
  }
}
